import capitalize from 'lodash/capitalize'

import {
  Flex,
  Text,
  Link,
  Button,
  Badge,
  Card,
  UsersIcon,
  Heading,
  CriticalIcon,
  AllChannelsIcon,
} from '@buffer-mono/popcorn'

import { graphql, readFragment } from '~publish/graphql'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import type { FragmentOf } from '~publish/graphql'
import { HC_UTM_PARAMS } from '~publish/legacy/utils/contants'

import PlanCard from './PlanCard'
import { getUsersCopy } from '../utils/helpers'

import styles from './PlanContent.module.css'
import inlineTextControls from '../../shared/inlineTextControls.module.css'

import type { JSX } from 'react'

export const MobileBilling_Organization = graphql(/* GraphQL */ `
  fragment MobileBilling_Organization on Organization {
    channels {
      isLocked
    }
    members {
      totalCount
    }
    limits {
      channels
    }
  }
`)

export const MobileBilling_MobileBillingDetails = graphql(/* GraphQL */ `
  fragment MobileBilling_MobileBillingDetails on MobileBillingDetails {
    platform
    subscription {
      periodEnd
      isCanceledAtPeriodEnd
      canceledAt
      trial {
        startDate
        endDate
      }
      plan {
        id
        name
        interval
        limits {
          members
        }
        pricing {
          unitPrice
        }
      }
    }
  }
`)

const formatDate = (date: string): string =>
  new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

export const MobileBilling = ({
  organization,
  mobileBilling,
}: {
  organization: FragmentOf<typeof MobileBilling_Organization>
  mobileBilling: FragmentOf<typeof MobileBilling_MobileBillingDetails>
}): JSX.Element => {
  const openGlobalModal = useOpenGlobalModal()
  const org = readFragment(MobileBilling_Organization, organization)
  const billing = readFragment(
    MobileBilling_MobileBillingDetails,
    mobileBilling,
  )

  const { platform, subscription } = billing
  const { plan, trial, periodEnd, canceledAt, isCanceledAtPeriodEnd } =
    subscription

  const connectedChannelsCount = org.channels.length
  const lockedChannelsCount = org.channels.filter(
    (channel) => channel.isLocked,
  ).length
  const channelsLimit = org.limits.channels

  const now = new Date()
  const isOnTrial =
    !!trial && new Date(trial.startDate) <= now && now < new Date(trial.endDate)

  const renewalDate = periodEnd ? formatDate(periodEnd) : null
  const canceledDate = canceledAt ? formatDate(canceledAt) : null

  const gatewayStoreName =
    platform === 'android'
      ? 'Google Play Store'
      : platform === 'apple'
        ? 'Apple App Store'
        : 'Buffer App'

  const usersCopy = getUsersCopy(plan.limits.members ?? 0)

  const pricingTier = plan.pricing[0]
  const pricePerChannel = pricingTier ? pricingTier.unitPrice / 100 : null
  const channelPriceCopy =
    pricePerChannel === null
      ? null
      : plan.interval === 'year'
        ? `$${pricePerChannel} each per year`
        : `$${pricePerChannel} each per month`

  const handleChangePlan = (): void => {
    openGlobalModal(MODALS.planSelector, {
      cta: 'billing-plansCard-changePlanFromMobile-1',
      upgradePathName: 'billing-mobile-changePlan',
    })
  }

  return (
    <Flex direction="column" gap="md" fullWidth>
      {/* Header */}
      <Flex direction="row" fullWidth>
        {/* Plan details */}
        <Flex direction="column" gap="xs" fullWidth>
          {/* Plan name and trial status */}
          <Flex direction="row" gap="xs" align="center">
            <Heading as="h2" size="small">
              Plan:
            </Heading>
            <Badge
              size="medium"
              variant="brand-subtle"
              style={{ textTransform: 'uppercase' }}
            >
              {plan.name}
            </Badge>
            {isOnTrial && (
              <Badge
                size="medium"
                variant="neutral"
                style={{ textTransform: 'uppercase' }}
              >
                On Trial
              </Badge>
            )}
          </Flex>
          {/* Paid plans canceled at period end */}
          {isCanceledAtPeriodEnd && (
            <li>
              <Text size="md">
                {canceledDate && `Your plan was changed on ${canceledDate}. `}
                You can continue using the{' '}
                <strong>{capitalize(plan.name)}</strong> features until the end
                of your billing cycle on <strong>{renewalDate}</strong>, when
                your Free subscription starts.
              </Text>
            </li>
          )}
          {/* Renewal date */}
          {!isCanceledAtPeriodEnd && plan.id !== 'free' && (
            <ul className={styles.planCardList}>
              <li>
                <Text size="md">
                  Billed {plan.interval}ly. Renews on{' '}
                  <strong>{renewalDate}</strong>
                </Text>
              </li>
            </ul>
          )}
        </Flex>
        {/* Actions */}
        <Flex direction="row" gap="2xs" align="center">
          <Button size="medium" variant="secondary" onClick={handleChangePlan}>
            Change Plan
          </Button>
        </Flex>
      </Flex>
      {/* 2-column plan details card */}
      <Card className={styles.planCard}>
        <Flex direction="row" align="center" justify="around">
          {/* Users */}
          <PlanCard>
            <PlanCard.Icon>
              <UsersIcon />
            </PlanCard.Icon>
            <PlanCard.Title>Users</PlanCard.Title>
            <PlanCard.Info>{usersCopy}</PlanCard.Info>
            <PlanCard.Value>{org.members.totalCount}</PlanCard.Value>
          </PlanCard>

          {/* Channels */}
          <PlanCard>
            <PlanCard.Icon>
              <AllChannelsIcon />
            </PlanCard.Icon>
            <PlanCard.Title>Channels</PlanCard.Title>
            {channelPriceCopy && (
              <PlanCard.Info>{channelPriceCopy}</PlanCard.Info>
            )}
            <PlanCard.Value>
              {isOnTrial
                ? `${connectedChannelsCount}`
                : `${connectedChannelsCount}/${channelsLimit}`}
            </PlanCard.Value>
            {lockedChannelsCount > 0 && (
              <PlanCard.FootNote>
                <Text
                  size="sm"
                  color="critical"
                  icon={<CriticalIcon color="critical" />}
                >
                  {lockedChannelsCount} channel
                  {lockedChannelsCount !== 1 ? 's' : ''} locked
                </Text>
              </PlanCard.FootNote>
            )}
          </PlanCard>
        </Flex>
      </Card>
      <Flex direction="column" align="center" gap="lg" fullWidth>
        <Text size="md" align="center" weight="bold" lineHeight="regular">
          Your subscription is managed through the {gatewayStoreName}.{' '}
          <Link
            external
            href={`https://support.buffer.com/hc/en-us/articles/360038768454?${HC_UTM_PARAMS}`}
          >
            Learn more
          </Link>
        </Text>
        <Text size="md" align="center">
          Need help?{' '}
          <Button
            type="button"
            variant="tertiary"
            size="small"
            className={inlineTextControls.inlineButton}
            onClick={(): void => {
              window.Beacon?.('navigate', '/ask/message/')
            }}
          >
            Talk to our support team
          </Button>
        </Text>
      </Flex>
    </Flex>
  )
}
